/**
 * Gjenbrukbar inline-term med hover-popover + "Les mer"-modal.
 *
 * Henter innhold fra databasen (help_articles via /api/help) basert på `slug`.
 * Hover viser `summary`, "Les mer" åpner full modal med `body_markdown` rendret
 * via MarkdownRenderer (samme som HelpPanel).
 *
 * Systemadmin kan oppdatere innhold via /settings/help-articles uten kode-deploy —
 * endringer slår umiddelbart gjennom i alle veiviser-steder som bruker samme slug.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Shield, Loader2 } from 'lucide-react';
import { getArticle, logView, type HelpArticle } from '../../lib/help';
import MarkdownRenderer from '../help/MarkdownRenderer';

export interface InlineHelpTermProps {
  /** help_articles.slug — f.eks. 'wizard-seb-browser' */
  slug: string;
  /** Teksten brukeren leser inline. Hvis ikke oppgitt, bruker artikkelens title. */
  label?: React.ReactNode;
  /** Rute til context-logging (hvor i app-en termen vises) */
  route?: string;
}

export default function InlineHelpTerm({ slug, label, route }: InlineHelpTermProps) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);

  const loadArticle = () => {
    if (article || loading) return;
    setLoading(true);
    setError(null);
    getArticle(slug, 'nb')
      .then((a) => setArticle(a))
      .catch((e) => setError(e instanceof Error ? e.message : 'Kunne ikke laste'))
      .finally(() => setLoading(false));
  };

  const openHover = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setHoverOpen(true);
    loadArticle();
  };
  const closeHoverSoon = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setHoverOpen(false), 180);
  };

  const openModal = () => {
    setHoverOpen(false);
    setModalOpen(true);
    loadArticle();
    void logView(slug, 'panel', route);
  };

  // Lukk popover ved klikk utenfor
  useEffect(() => {
    if (!hoverOpen) return;
    const h = (e: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        setHoverOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [hoverOpen]);

  // Esc lukker modal
  useEffect(() => {
    if (!modalOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setModalOpen(false); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [modalOpen]);

  const displayLabel = label ?? article?.title ?? slug;

  return (
    <>
      <span
        ref={anchorRef}
        className="relative inline-block"
        onMouseEnter={openHover}
        onMouseLeave={closeHoverSoon}
        onFocus={openHover}
        onBlur={closeHoverSoon}
      >
        <button
          type="button"
          onClick={() => setHoverOpen((v) => !v)}
          className="underline decoration-dotted underline-offset-2 decoration-kf-blue-400 hover:decoration-kf-blue-700 focus:outline-none focus:ring-2 focus:ring-kf-blue-300 rounded"
          aria-describedby={`help-popover-${slug}`}
        >
          {displayLabel}
        </button>

        {hoverOpen && (
          <span
            id={`help-popover-${slug}`}
            role="tooltip"
            className="absolute z-[9500] top-full left-0 mt-1 w-80 max-w-[90vw] bg-white border border-kf-gray-200 rounded-lg shadow-xl p-3 text-left"
            onMouseEnter={openHover}
            onMouseLeave={closeHoverSoon}
          >
            {loading && (
              <div className="flex items-center gap-2 text-xs text-kf-gray-600">
                <Loader2 className="w-3 h-3 animate-spin" />
                Laster forklaring…
              </div>
            )}
            {error && (
              <div className="text-xs text-red-700">
                <div className="font-semibold">Fant ikke hjelp</div>
                <div className="text-[11px] mt-0.5 text-red-600">
                  Slug: <code>{slug}</code>
                </div>
              </div>
            )}
            {article && (
              <>
                <div className="flex items-start gap-2 mb-1">
                  <Shield className="w-4 h-4 text-kf-blue-600 shrink-0 mt-0.5" />
                  <div className="text-sm font-semibold text-kf-gray-900">{article.title}</div>
                </div>
                <p className="text-xs text-kf-gray-700 leading-relaxed mb-2">{article.summary}</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal();
                  }}
                  className="text-xs font-semibold text-kf-blue-700 hover:text-kf-blue-900 underline"
                >
                  Les mer her →
                </button>
              </>
            )}
          </span>
        )}
      </span>

      {modalOpen && createPortal(
        <div
          className="fixed inset-0 z-[9800] flex items-center justify-center p-4 bg-kf-gray-900/60 backdrop-blur-[2px]"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalOpen(false);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`help-modal-title-${slug}`}
          >
            <header className="px-5 py-4 border-b border-kf-gray-200 bg-kf-blue-50 rounded-t-xl flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <Shield className="w-6 h-6 text-kf-blue-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h2 id={`help-modal-title-${slug}`} className="text-lg font-semibold text-kf-gray-900">
                    {article?.title || 'Laster…'}
                  </h2>
                  {article?.summary && (
                    <p className="text-sm text-kf-gray-600 mt-0.5">{article.summary}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="shrink-0 p-1.5 rounded hover:bg-white text-kf-gray-600"
                aria-label="Lukk"
              >
                <X className="w-5 h-5" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-kf-gray-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Laster artikkel…
                </div>
              )}
              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                  Kunne ikke laste artikkelen: {error}. Slug: <code className="text-xs">{slug}</code>
                </div>
              )}
              {article && (
                <div className="prose prose-sm max-w-none">
                  <MarkdownRenderer content={article.body_markdown} />
                </div>
              )}
            </div>

            <footer className="px-5 py-3 border-t border-kf-gray-200 bg-kf-gray-50 rounded-b-xl flex items-center justify-between gap-3">
              <div className="text-xs text-kf-gray-500">
                Innhold forvaltes av systemadmin. Innspill til tekst?{' '}
                <span className="italic">Les mer-knappen i hjelpe-menyen øverst.</span>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-1.5 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded"
              >
                Lukk
              </button>
            </footer>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
